import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { ADMIN_API_BASE_URL, ApiError, Button, Skeleton } from '../../core/index.js'
import { ScheduleBoardReadOnly } from '../../schedule/index.js'
import { getScheduleBoard, getScheduleCoverage, type PartnerSession } from '../lib/partnerApi.js'
import { ErrorNote, describeError } from './formControls.js'

/** Plays a partner's scheduler reading the released month's board: what moved after the release, as `GET /facilities/{facilityId}/schedule/board` answers it. */
export function ScheduleBoardView({ session, facilityId }: { session: PartnerSession; facilityId: string }) {
  const query = useQuery({
    queryKey: ['partner-simulator', 'schedule-board', session.clientId, facilityId],
    queryFn: () => getScheduleBoard(ADMIN_API_BASE_URL ?? '', session, facilityId),
    enabled: Boolean(ADMIN_API_BASE_URL),
    retry: false,
  })
  // GET /facilities/{facilityId}/schedule/coverage, read beside the board: the month against the facility's minimum staffing.
  const coverage = useQuery({
    queryKey: ['partner-simulator', 'schedule-coverage', session.clientId, facilityId, query.data?.period ?? null],
    queryFn: () => getScheduleCoverage(ADMIN_API_BASE_URL ?? '', session, facilityId, query.data?.period),
    enabled: Boolean(ADMIN_API_BASE_URL) && query.isSuccess,
    retry: false,
  })
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-mid">The released month's board as a partner reads it: the change window's requests, the swaps, the open board's postings and the ledger of every change. Reading moves the board to now.</p>
        <Button icon={RefreshCw} variant="secondary" size="sm" disabled={query.isFetching} onClick={() => void Promise.all([query.refetch(), coverage.refetch()])}>
          Refresh
        </Button>
      </div>
      {query.isPending ? <Skeleton /> : query.isError ? query.error instanceof ApiError && query.error.status === 409 ? <p className="text-xs text-ink-mid">The board opens with the month's release; nothing is released yet.</p> : <ErrorNote message={describeError(query.error, 'The board could not be read.')} onRetry={() => void query.refetch()} retrying={query.isFetching} /> : <ScheduleBoardReadOnly board={query.data} coverage={coverage.data ?? null} />}
    </div>
  )
}
